import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

/**
 * Button primitive — Studio Linen design system v1.0.
 *
 * Variants (8):
 *   primary    : ink solid + 흰 글자.        Most pages' main CTA.
 *   accent     : terracotta solid.            Signature CTA (signup hero, share credential).
 *   secondary  : transparent + ink border.    Companion to primary.
 *   ghost      : no border, hover paper.      In-page navigate.
 *   danger     : brick solid.                 Destructive (revoke, delete).
 *   link       : underline 1px.               Inline text actions.
 *
 * Sizes (4):
 *   sm  28px / label 11px       (compact actions, table rows)
 *   md  32px / label 11px       (default — most pages)
 *   lg  40px / body 13px        (form submit)
 *   xl  48px / body 15px        (hero CTA)
 *
 * States: default / hover / active / focus-visible / disabled / loading
 *
 * `asChild` — render via Link or any custom element by passing one child.
 *   <Button asChild><Link to="/x">Go</Link></Button>
 *
 * `loading` — replaces icon area with inline spinner; text muted.
 */

export type ButtonVariant =
  | 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger' | 'link';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  children?: ReactNode;
  /** Render the single child element instead of a <button>. Useful for <Link>. */
  asChild?: boolean;
  /** Full width */
  block?: boolean;
}

function buildClassName({
  variant = 'primary',
  size = 'md',
  loading = false,
  block = false,
  disabled,
  className,
}: Pick<ButtonProps, 'variant' | 'size' | 'loading' | 'block' | 'disabled' | 'className'>) {
  return [
    'bq-btn',
    `bq-btn--${variant}`,
    `bq-btn--${size}`,
    block ? 'bq-btn--block' : '',
    loading ? 'is-loading' : '',
    disabled ? 'is-disabled' : '',
    className ?? '',
  ].filter(Boolean).join(' ');
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    iconLeft,
    iconRight,
    children,
    asChild = false,
    block = false,
    className,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const cn = buildClassName({ variant, size, loading, block, disabled, className });

  const content = (
    <>
      {loading ? (
        <span className="bq-btn__spinner" aria-hidden="true" />
      ) : iconLeft ? (
        <span className="bq-btn__icon" aria-hidden="true">{iconLeft}</span>
      ) : null}
      {children != null && <span className="bq-btn__label">{children}</span>}
      {!loading && iconRight && (
        <span className="bq-btn__icon" aria-hidden="true">{iconRight}</span>
      )}
    </>
  );

  if (asChild) {
    // Clone the single child, merging className + aria.
    // Caller is responsible for passing exactly one valid React element.
    // We don't pull cloneElement here to keep the dep surface tiny; the
    // pattern is: <Button asChild><Link>...</Link></Button>.
    // We render a span wrapper so styling is preserved if cloneElement-less.
    return (
      <span className={cn} role="presentation" {...(rest as object)}>
        {content}
      </span>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      className={cn}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
});
